import { Box, Button, CircularProgress, Divider, Typography } from '@mui/material'
import React, { Fragment, useEffect, useState, useCallback } from 'react'
import NotificationItemList from '../notificationItemList'
import { useTranslation } from 'react-i18next'
import { useCustomNavigation } from '../../../hooks/useCustomNavigate'
import { INotifications } from '../../../utils/types'
import instance from '../../../providers/axios.config'
import Notiflix from 'notiflix'
import { ROUTES } from '../../../utils/constants'
import LoadingNotification from './LoadingNotification'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../redux/reducers/rootReducer'
import { updateNotifications } from '../../../redux/reducers/usersReducer'
import styles from './All.module.scss'

type AllType = {
  isHeader?: boolean
}

export const All = (props: AllType) => {
  const { isHeader } = props
  const { t } = useTranslation()
  const customNavigate = useCustomNavigation()
  const dispatch = useDispatch()
  const profile = useSelector((state: RootState) => state.user.usersData)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [notifications, setNotifications] = useState<INotifications[]>([])
  const [newNotifications, setNewNotifications] = useState<INotifications[]>([])
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    totalPages: 1
  })

  const fetchNotifications = useCallback(async (page: number, append: boolean = false) => {
    if (page === 1) {
      setIsLoading(true)
    } else {
      setIsLoadingMore(true)
    }

    try {
      const res = await instance.get(`notifications?limit=${pagination.perPage}&page=${page}`) as any
      
      if (append) {
        // Concatenate new results with existing ones
        setNotifications(prev => [...prev, ...res.data.notifications.old])
        setNewNotifications(prev => [...prev, ...res.data.notifications.new])
      } else {
        setNotifications(res.data.notifications.old)
        setNewNotifications(res.data.notifications.new)
      }
      
      setPagination({ ...res?.data?.pagination })
    } catch (error) {
      console.error('Error fetching data:', error)
      Notiflix.Report.failure(
        'Error',
        'Failed to fetch data. Please try again later.',
        'OK'
      )
    } finally {
      setIsLoading(false)
      setIsLoadingMore(false)
    }
  }, [pagination.perPage])

  useEffect(() => {
    fetchNotifications(1, false)
  }, [])

  const handleShowMore = () => {
    if (pagination.currentPage < pagination.totalPages) {
      fetchNotifications(pagination.currentPage + 1, true)
    }
  }

  const handleReadNotification = async (id: number) => {
    Notiflix.Loading.standard({ svgColor: '#c11b1e' })
    try {
      const resp = await instance.put(`notifications/${id}`) as any
      if (resp.status === 'success') {
        const updateNotification = notifications.map((not) => {
          if (not.id === id) {
            return { ...not, isRead: 1 }
          }
          return { ...not }
        })
        const updateNotificationNew = newNotifications.map((not) => {
          if (not.id === id) {
            return { ...not, isRead: 1 }
          }
          return { ...not }
        })
        setNotifications([...updateNotification])
        setNewNotifications([...updateNotificationNew])
        Notiflix.Loading.remove()
      } else {
        Notiflix.Loading.remove()
        Notiflix.Report.failure('Error', `${resp?.message}`, 'OK')
      }
    } catch (e) {
      Notiflix.Loading.remove()
      Notiflix.Report.failure('Error', `${t('error_api')}`, 'OK')
    }
  }

  const handleDeleteNotification = async (id: number) => {
    Notiflix.Loading.standard({ svgColor: '#c11b1e' })
    try {
      const resp = await instance.delete(`notifications/${id}`) as any
      if (resp.status === 'success') {
        const updateNotification = notifications.filter((notification) => notification.id !== id)
        const updateNotificationNew = newNotifications.filter((notification) => notification.id !== id)
        setNewNotifications([...updateNotificationNew])
        setNotifications([...updateNotification])
        Notiflix.Loading.remove()
        Notiflix.Notify.success(`${resp?.message}`)
      } else {
        Notiflix.Loading.remove()
        Notiflix.Report.failure('Error', `${resp?.message}`, 'OK')
      }
    } catch (e) {
      Notiflix.Loading.remove()
      Notiflix.Report.failure('Error', `${t('error_api')}`, 'OK')
    }
  }

  const handleRedirect = () => {
    customNavigate(ROUTES.NOTIFICATIONS)
  }

  const handleDisabledNotifications = async (type: any) => {
    Notiflix.Loading.standard({ svgColor: '#c11b1e' })
    const findDataNotifications = (profile?.notificationsDisabled || []).find((not: string) => not === type)
    try {
      const resp = await instance.put(
        `notifications/${type}/toggle`,
        JSON.stringify({ action: findDataNotifications ? 'enable' : 'disable' })
      ) as any
      if (resp.status === 'success') {
        if (findDataNotifications) {
          dispatch(updateNotifications((profile?.notificationsDisabled || []).filter((d: string) => d !== type)))
        } else {
          dispatch(updateNotifications(profile?.notificationsDisabled?.length ? [...profile?.notificationsDisabled, type] : [type]))
        }
        Notiflix.Loading.remove()
      } else {
        Notiflix.Loading.remove()
        Notiflix.Report.failure('Error', `${resp?.message}`, 'OK')
      }
    } catch (e) {
      Notiflix.Loading.remove()
      Notiflix.Report.failure('Error', `${t('error_api')}`, 'OK')
    }
  }

  const hasMorePages = pagination.currentPage < pagination.totalPages

  if (isLoading) {
    return (
      <Box className={`${styles.notificationList} ${isHeader ? styles.isHeader : ''}`}>
        {[1, 2, 3, 4].map((number) => (
          <Fragment key={number}>
            <LoadingNotification isHeader={isHeader} />
          </Fragment>
        ))}
      </Box>
    )
  }

  if (!notifications?.length && !newNotifications?.length && !isLoading) {
    return (
      <Box className={`${styles.emptyState} ${isHeader ? styles.isHeader : ''}`}>
        <Typography className={styles.emptyText}>
          {t('no_notification')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box className={`${styles.notificationList} ${isHeader ? styles.isHeader : ''}`}>
      {/* New Notifications Section */}
      {newNotifications?.length > 0 && (
        <Box className={styles.section}>
          <Box className={styles.sectionHeader}>
            <Typography className={`${styles.sectionTitle} ${isHeader ? styles.isHeader : ''}`}>
              {t('new')}
            </Typography>
            {isHeader && (
              <Button 
                onClick={handleRedirect} 
                className={styles.seeAllButton}
                size="small"
              >
                {t('see_all')}
              </Button>
            )}
          </Box>
          <Box className={styles.notificationItems}>
            {newNotifications.map((not: INotifications, index: number) => (
              <Fragment key={not.id}>
                <NotificationItemList
                  notification={not}
                  isHeader={isHeader}
                  onReadNotification={handleReadNotification}
                  onDeleteNotification={handleDeleteNotification}
                  onDisableNotification={handleDisabledNotifications}
                />
                {index < newNotifications.length - 1 && (
                  <Divider className={`${styles.divider} ${isHeader ? styles.isHeader : ''}`} />
                )}
              </Fragment>
            ))}
          </Box>
        </Box>
      )}

      {/* Divider between new and older sections */}
      {newNotifications?.length > 0 && notifications?.length > 0 && (
        <Divider className={`${styles.sectionDivider} ${isHeader ? styles.isHeader : ''}`} />
      )}

      {/* Older Notifications Section */}
      {notifications?.length > 0 && (
        <Box className={styles.section}>
          <Box className={styles.sectionHeader}>
            <Typography className={`${styles.sectionTitle} ${isHeader ? styles.isHeader : ''}`}>
              {t('older')}
            </Typography>
            {isHeader && !newNotifications?.length && (
              <Button 
                onClick={handleRedirect} 
                className={styles.seeAllButton}
                size="small"
              >
                {t('see_all')}
              </Button>
            )}
          </Box>
          <Box className={styles.notificationItems}>
            {notifications.map((notification: INotifications, index: number) => (
              <Fragment key={notification?.id}>
                <NotificationItemList
                  notification={notification}
                  isHeader={isHeader}
                  onReadNotification={handleReadNotification}
                  onDeleteNotification={handleDeleteNotification}
                  onDisableNotification={handleDisabledNotifications}
                />
                {index < notifications.length - 1 && (
                  <Divider className={`${styles.divider} ${isHeader ? styles.isHeader : ''}`} />
                )}
              </Fragment>
            ))}
          </Box>
        </Box>
      )}

      {/* Show More Button */}
      {hasMorePages && (
        <Box className={styles.showMoreContainer}>
          <Button
            variant="text"
            onClick={handleShowMore}
            disabled={isLoadingMore}
            className={`${styles.showMoreButton} ${isHeader ? styles.isHeader : ''}`}
          >
            {isLoadingMore ? (
              <CircularProgress size={20} sx={{ color: isHeader ? '#CBC7C2' : '#c11b1e' }} />
            ) : (
              t('show_more')
            )}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default All